/**
 * Inbound mailboxes — Step 20's admin surface.
 *
 * The screen exists to answer one question quickly: IS INTAKE WORKING? A
 * mailbox that quietly stopped polling is how a helpdesk loses a week of
 * tickets and only finds out when a customer phones. So each row leads with
 * its last poll, its last error, and what it has actually produced — and
 * there is a "Poll now" button that turns the question into an answer
 * instead of a wait.
 *
 * Draft-first on purpose: the only free text is a single-field inline add (an
 * email address). A one-field add is not a draft: there is nothing to half-
 * lose, and a Keep/Discard prompt over one abandoned address is noise.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Inbox, Play, Plus, Trash2 } from 'lucide-react';
import { apiFetch, apiSend } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { qk } from '../../lib/queryClient';
import { relativeTime } from '../../lib/time';
import { useConfirm } from '../../lib/confirm';

interface Mailbox {
  id: string;
  address: string;
  name: string | null;
  defaultType: string;
  isActive: number;
  lastPolledAt: number | null;
  lastError: string | null;
  lastErrorAt: number | null;
  counts: Record<string, number>;
}

const api = {
  list: () => apiFetch<{ entries: Mailbox[] }>('/api/admin/mail-settings/mailboxes'),
  create: (body: { address: string; name?: string; defaultType?: string }) =>
    apiSend<{ id: string }>('/api/admin/mail-settings/mailboxes', 'POST', body),
  update: (id: string, body: Record<string, unknown>) =>
    apiSend(`/api/admin/mail-settings/mailboxes/${id}`, 'PATCH', body),
  remove: (id: string) => apiSend(`/api/admin/mail-settings/mailboxes/${id}`, 'DELETE'),
  poll: (id: string) =>
    apiSend<{ fetched: number; created: number; appended: number; skipped: number; failed: number }>(
      `/api/admin/mail-settings/mailboxes/${id}/poll`,
      'POST',
    ),
};

export function MailboxesPage() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.mailboxes,
    queryFn: api.list,
  });
  const [adding, setAdding] = useState(false);
  const [address, setAddress] = useState('');
  const [type, setType] = useState('incident');
  const [formError, setFormError] = useState<string | null>(null);
  const [pollResult, setPollResult] = useState<Record<string, string>>({});

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.mailboxes });

  const create = useMutation({
    mutationFn: () => api.create({ address: address.trim(), defaultType: type }),
    onSuccess: (res) => {
      if (!res.ok) {
        setFormError(res.error ?? 'Could not add that mailbox');
        return;
      }
      setAdding(false);
      setAddress('');
      setFormError(null);
      invalidate();
    },
  });

  const poll = useMutation({
    mutationFn: (id: string) => api.poll(id),
    onSuccess: (res, id) => {
      const s = res.body;
      setPollResult({
        [id]:
          res.ok && s
            ? `${s.fetched} fetched · ${s.created} new · ${s.appended} replies · ${s.skipped} skipped${s.failed ? ` · ${s.failed} failed` : ''}`
            : (res.error ?? 'Poll failed'),
      });
      invalidate();
    },
  });

  if (isPending) return <Skeleton className="m-6 h-64 w-full max-w-4xl" />;
  if (isError)
    return (
      <div className="m-6 max-w-3xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load mailboxes</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  return (
    <div className="max-w-4xl p-6">
      <div className="mb-1 flex items-center gap-3">
        <Inbox className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Inbound mailboxes</h1>
        <div className="flex-1" />
        <Button size="sm" onClick={() => setAdding((o) => !o)}>
          <Plus /> Add mailbox
        </Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Mail sent to these addresses becomes tickets. Replies thread onto the original ticket by their message
        headers, falling back to the <code>[#1042]</code> token in the subject.
      </p>

      {adding && (
        <div className="mb-4 space-y-2 rounded-md border bg-muted/20 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              autoFocus
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="support@yourdomain.org"
              className="h-9 flex-1"
            />
            <Select value={type} onChange={(e) => setType(e.target.value)} className="h-9 w-44">
              <option value="incident">Raises incidents</option>
              <option value="service_request">Raises service requests</option>
            </Select>
            <Button size="sm" disabled={!address.trim() || create.isPending} onClick={() => create.mutate()}>
              Add
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            It starts switched off. Turning polling on for a mailbox with thousands of old messages would open
            thousands of tickets, so that is a deliberate second step.
          </p>
          {formError && <p className="text-xs text-destructive">{formError}</p>}
        </div>
      )}

      {data!.entries.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          No mailboxes yet. Add the address your customers already email.
        </div>
      ) : (
        <div className="space-y-3">
          {data!.entries.map((m) => (
            <div key={m.id} className="rounded-lg border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{m.address}</span>
                {m.isActive ? (
                  <Badge variant="outline" className="gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Polling
                  </Badge>
                ) : (
                  <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">
                    Off
                  </Badge>
                )}
                <Select
                  value={m.isActive ? 'on' : 'off'}
                  onChange={async (e) => {
                    await api.update(m.id, { isActive: e.target.value === 'on' });
                    invalidate();
                  }}
                  className="h-8 w-28 text-xs"
                  aria-label={`Polling for ${m.address}`}
                >
                  <option value="off">Off</option>
                  <option value="on">On</option>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={poll.isPending}
                  onClick={() => poll.mutate(m.id)}
                >
                  <Play /> Poll now
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-destructive/40 text-destructive hover:bg-destructive/10"
                  onClick={async () => {
                    if (
                      !(await confirm({
                        title: `Stop reading ${m.address}?`,
                        body: 'The intake history is kept.',
                        destructive: true,
                        confirmLabel: 'Stop reading it',
                      }))
                    )
                      return;
                    await api.remove(m.id);
                    invalidate();
                  }}
                  aria-label={`Remove ${m.address}`}
                >
                  <Trash2 />
                </Button>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>Raises {m.defaultType === 'service_request' ? 'service requests' : 'incidents'}</span>
                <span>{m.lastPolledAt ? `Last polled ${relativeTime(m.lastPolledAt)}` : 'Never polled'}</span>
                {(m.counts.processed ?? 0) > 0 && <span>{m.counts.processed} messages processed</span>}
                {(m.counts.skipped ?? 0) > 0 && <span>{m.counts.skipped} skipped</span>}
                {(m.counts.failed ?? 0) > 0 && (
                  <span className="text-destructive">{m.counts.failed} failed</span>
                )}
              </div>

              {pollResult[m.id] && <p className="mt-2 text-xs text-muted-foreground">{pollResult[m.id]}</p>}

              {/* The most important line on the page: a mailbox that stopped
                  working says so here rather than failing silently. */}
              {m.lastError && (
                <p className="mt-2 flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    {m.lastError}
                    {m.lastErrorAt ? ` (${relativeTime(m.lastErrorAt)})` : ''}
                  </span>
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
